// Phase 22B — Field-side TA/DA screen.
//
// Landing: "My Claims" — list + big Add-today button
// Editor: header (date, market, DA line) + Add travel / Add night stay /
//         Add other buttons + item list with per-item edit/delete + Submit.
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Plus, ChevronLeft, Loader2, Trash2, Pencil, Bike, Package, Wallet,
  MapPin, Camera, ClipboardList, Moon,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

const STATUS_STYLE = {
  draft:            "bg-neutral-100 text-neutral-700 border-neutral-200",
  submitted:        "bg-amber-100 text-amber-800 border-amber-200",
  manager_approved: "bg-sky-100 text-sky-800 border-sky-200",
  admin_approved:   "bg-emerald-100 text-emerald-800 border-emerald-200",
  rejected:         "bg-rose-100 text-rose-800 border-rose-200",
};

function todayIst() {
  const d = new Date(Date.now() + 5.5 * 3600 * 1000);
  return d.toISOString().slice(0, 10);
}

// Shows what the SE actually gets once a manager/admin has acted on this
// item — not just what they claimed. Previously every item always showed
// only the claimed figure, so a partial approval (SE claims ₹150, ASM
// approves ₹120) never surfaced to the SE at all.
//
// TA/DA is a 2-stage approval (ASM/manager, then admin) — each stage now
// records its OWN amount (manager_approved_amount_inr / admin_approved_
// amount_inr) instead of the second stage silently overwriting the first's
// decision. Show both whenever the manager has acted, so the SE sees the
// full bifurcation: what they claimed → what the ASM approved → what admin
// finally approved (only once admin has actually acted too).
function ItemAmount({ claimed, item }) {
  const status = item.per_item_status;
  if (status === "rejected") {
    return (
      <div className="flex items-center gap-2">
        <span className="text-sm text-neutral-400 line-through">₹ {claimed}</span>
        <Badge className="bg-rose-100 text-rose-800 border-0 text-[10px]">Rejected</Badge>
      </div>
    );
  }
  if (status === "approved") {
    const mgrAmt = item.manager_approved_amount_inr;
    const adminAmt = item.admin_approved_amount_inr;
    const hasMgr = mgrAmt != null;
    const hasAdmin = adminAmt != null;
    const mgrChanged = hasMgr && Number(mgrAmt) !== Number(claimed);
    const adminChanged = hasAdmin && hasMgr && Number(adminAmt) !== Number(mgrAmt);
    return (
      <div className="space-y-0.5">
        <div className="flex items-center gap-2 flex-wrap">
          {(mgrChanged || adminChanged) && <span className="text-sm text-neutral-400 line-through">₹ {claimed}</span>}
          {hasMgr && (
            <span className={"text-sm font-bold " + (hasAdmin ? "text-neutral-400 line-through" : "text-emerald-700")}>
              ₹ {mgrAmt}
            </span>
          )}
          {hasAdmin && <span className="text-lg font-bold text-emerald-700">₹ {adminAmt}</span>}
          <Badge className="bg-emerald-100 text-emerald-800 border-0 text-[10px]">
            {hasAdmin ? "Admin approved" : "ASM approved"}
          </Badge>
        </div>
        {hasMgr && !hasAdmin && (
          <div className="text-[10px] text-neutral-500">Awaiting admin approval</div>
        )}
      </div>
    );
  }
  return <div className="text-lg font-bold">₹ {claimed}</div>;
}

export default function TadaScreen() {
  // ?date=YYYY-MM-DD — deep-linked from the Attendance screen's "Complete
  // Day End" flow, which requires that day's TA/DA claim (or an expense —
  // no creation endpoint exists for that) before Day End will accept it.
  // The backend allows this exact date regardless of the org's normal
  // backdate window (routes_tada.py::_claim_date_unblocks_day_end); the
  // date picker below must therefore also be usable for it even when the
  // org's own max_backdate_days is 0 (the default) and would otherwise hide
  // the picker entirely, leaving no way to pick anything but today.
  const [searchParams] = useSearchParams();
  const targetDate = searchParams.get("date") || null;

  const [claims, setClaims] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openClaimId, setOpenClaimId] = useState(null);
  const [creating, setCreating] = useState(false);
  const [cfg, setCfg] = useState({ effective_max_backdate_days: 0 });
  const [claimDate, setClaimDate] = useState(targetDate || todayIst());
  const maxBackdateDays = cfg.effective_max_backdate_days
    ?? cfg.max_backdate_days
    ?? (cfg.allow_next_day_backfill ? 1 : 0);
  const earliestClaimDate = (() => {
    const d = new Date(Date.now() + 5.5 * 3600 * 1000);
    d.setDate(d.getDate() - maxBackdateDays);
    return d.toISOString().slice(0, 10);
  })();
  // Widen the picker's own min bound to include the deep-linked date when
  // it falls outside the org's normal window — the backend will accept it
  // either way, but the native date input's `min` would otherwise block
  // selecting it client-side.
  const pickerMinDate = targetDate && targetDate < earliestClaimDate ? targetDate : earliestClaimDate;
  const showDatePicker = maxBackdateDays > 0 || !!targetDate;
  // Arriving here via a "resolve this pending Day End" deep link is a
  // one-shot recovery action for that ONE past date — locking the picker to
  // it (rather than leaving the whole backdate window open) stops a rep from
  // accidentally filing today's or some other unrelated day's claim while
  // in this flow, which wouldn't unblock anything.
  const claimDateLocked = !!targetDate;

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const [c, cfgR, s] = await Promise.all([
        api.get("/tada/claims", { params: { page_size: 50 } }),
        api.get("/tada/config"),
        api.get("/tada/my-summary"),
      ]);
      setClaims(c.data.items || []);
      setCfg(cfgR.data || {});
      setSummary(s.data || null);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadList(); }, [loadList]);

  const createClaim = async (claim_date) => {
    setCreating(true);
    try {
      const r = await api.post("/tada/claims", { claim_date });
      toast.success("Claim created — DA auto-added");
      loadList();
      setOpenClaimId(r.data.id);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setCreating(false); }
  };

  if (openClaimId) {
    return <ClaimEditor claimId={openClaimId}
                          onClose={() => { setOpenClaimId(null); loadList(); }} />;
  }

  return (
    <div className="p-4 space-y-3 pb-24" data-testid="tada-screen">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <h1 className="text-lg font-extrabold text-neutral-900">TA / DA</h1>
          <p className="text-xs text-neutral-500">
            {targetDate ? (
              <>Submitting for <b>{targetDate}</b> — needed to close out that day's Day End.</>
            ) : (
              <>
                Submit today's claim before ending your day.{" "}
                {maxBackdateDays > 0
                  ? `Backdate up to ${maxBackdateDays} day${maxBackdateDays === 1 ? "" : "s"} allowed.`
                  : "Same-day only."}
              </>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {showDatePicker && (
            <input type="date" value={claimDate}
                   min={claimDateLocked ? targetDate : pickerMinDate}
                   max={claimDateLocked ? targetDate : todayIst()}
                   disabled={claimDateLocked}
                   onChange={(e) => !claimDateLocked && setClaimDate(e.target.value)}
                   className="h-9 px-2 rounded-lg border border-neutral-200 text-xs disabled:opacity-70 disabled:bg-neutral-50"
                   data-testid="tada-claim-date-picker" />
          )}
          <Button className="tulsi-gradient text-white h-11 rounded-xl"
                  onClick={() => createClaim(showDatePicker ? claimDate : todayIst())} disabled={creating}
                  data-testid="tada-add-today">
            {creating && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}
            <Plus className="h-4 w-4 mr-1.5" /> Add claim
          </Button>
        </div>
      </div>

      {loading && <div className="text-center py-8"><Loader2 className="h-5 w-5 animate-spin mx-auto text-neutral-400" /></div>}

      {/* Phase 22C — Dashboard tiles. Bifurcated per the 2-stage approval —
          a claim the ASM has signed off on (manager_approved, awaiting
          admin) used to vanish into "Pending" with no sign the ASM had
          already acted; now it gets its own tile. */}
      {summary && !loading && (
        <div className="grid grid-cols-2 gap-2" data-testid="tada-tiles">
          {[
            {k:"claimed", label:"Claimed", inr: summary.claimed_inr, n: summary.claimed, color:"text-neutral-800", bg:"bg-neutral-50 border-neutral-200"},
            {k:"manager-approved", label:"ASM Approved", inr: summary.manager_approved_inr, n: summary.manager_approved, color:"text-sky-700", bg:"bg-sky-50 border-sky-200"},
            {k:"admin-approved", label:"Admin Approved", inr: summary.admin_approved_inr, n: summary.admin_approved, color:"text-emerald-700", bg:"bg-emerald-50 border-emerald-200"},
            {k:"rejected", label:"Rejected", inr: null, n: summary.rejected, color:"text-rose-700", bg:"bg-rose-50 border-rose-200"},
            {k:"pending", label:"Pending", inr: summary.pending_inr, n: summary.pending, color:"text-amber-700", bg:"bg-amber-50 border-amber-200"},
          ].map(t => (
            <div key={t.k} className={"rounded-xl border p-3 " + t.bg} data-testid={`tada-tile-${t.k}`}>
              <div className="text-[10px] uppercase tracking-widest text-neutral-500">{t.label}</div>
              {t.inr != null && <div className={"text-lg font-black " + t.color}>₹ {t.inr.toLocaleString("en-IN")}</div>}
              <div className={"text-[11px] " + t.color}>{t.n} claim{t.n !== 1 ? "s" : ""}</div>
            </div>
          ))}
        </div>
      )}
      {!loading && claims.length === 0 && (
        <div className="text-center py-16 text-sm text-neutral-500" data-testid="tada-empty">
          No claims yet. Punch in first, then add today's TA/DA claim.
        </div>
      )}

      <div className="space-y-2">
        {claims.map(c => (
          <Card key={c.id} className="border-neutral-200 rounded-2xl cursor-pointer"
                onClick={() => setOpenClaimId(c.id)}
                data-testid={`tada-claim-row-${c.id}`}>
            <CardContent className="p-3 flex items-center justify-between">
              <div>
                <div className="text-sm font-semibold text-neutral-900">{c.claim_date}</div>
                <div className="text-[11px] text-neutral-500">
                  {c.market_type_code}
                  {c.submitted_at && ` · submitted ${new Date(c.submitted_at).toLocaleTimeString([], {hour:'2-digit',minute:'2-digit'})}`}
                </div>
                {c.rejection_reason && (
                  <div className="text-[11px] text-rose-700 mt-0.5">Rejected: {c.rejection_reason}</div>
                )}
              </div>
              <Badge className={"border text-[10px] " + (STATUS_STYLE[c.status] || STATUS_STYLE.draft)}
                     data-testid={`tada-claim-status-${c.id}`}>
                {c.status.replace("_", " ")}
              </Badge>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Editor
// ---------------------------------------------------------------------------
function ClaimEditor({ claimId, onClose }) {
  const [claim, setClaim] = useState(null);
  const [loading, setLoading] = useState(true);
  const [addingType, setAddingType] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  // Hotfix — draft/rejected claims had no way to be deleted from the UI at
  // all, even though the backend already supported it
  // (DELETE /tada/claims/{id}, restricted to draft/rejected server-side).
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/tada/claims/${claimId}`);
      setClaim(r.data);
    } catch (e) { toast.error(formatApiError(e)); onClose(); }
    finally { setLoading(false); }
  }, [claimId, onClose]);
  useEffect(() => { load(); }, [load]);

  const del = async (item_id) => {
    try {
      await api.delete(`/tada/claims/${claimId}/items/${item_id}`);
      toast.success("Removed");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      await api.post(`/tada/claims/${claimId}/submit`);
      toast.success("Submitted for approval");
      onClose();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSubmitting(false); }
  };

  const deleteClaim = async () => {
    setDeleting(true);
    try {
      await api.delete(`/tada/claims/${claimId}`);
      toast.success("Claim deleted");
      setConfirmDeleteOpen(false);
      onClose();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setDeleting(false); }
  };

  if (loading || !claim) {
    return <div className="p-8 text-center"><Loader2 className="h-5 w-5 animate-spin mx-auto text-neutral-400" /></div>;
  }

  const isEditable = ["draft", "rejected"].includes(claim.status);

  return (
    <div className="p-4 space-y-3 pb-32" data-testid="tada-editor">
      <div className="flex items-center justify-between">
        <button onClick={onClose} className="flex items-center text-sm text-neutral-600" data-testid="tada-editor-back">
          <ChevronLeft className="h-4 w-4" /> Back to My Claims
        </button>
        {isEditable && (
          <button onClick={() => setConfirmDeleteOpen(true)}
                  className="flex items-center gap-1 text-sm text-rose-600"
                  data-testid="tada-delete-claim-open">
            <Trash2 className="h-4 w-4" /> Delete
          </button>
        )}
      </div>

      <Dialog open={confirmDeleteOpen} onOpenChange={setConfirmDeleteOpen}>
        <DialogContent className="max-w-sm" data-testid="tada-delete-claim-dialog">
          <DialogHeader>
            <DialogTitle>Delete this claim?</DialogTitle>
            <DialogDescription>
              This removes the whole claim — the auto DA line and any travel
              or other items on it. This can't be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDeleteOpen(false)}
                    data-testid="tada-delete-claim-cancel">
              Cancel
            </Button>
            <Button variant="destructive" onClick={deleteClaim} disabled={deleting}
                    data-testid="tada-delete-claim-confirm">
              {deleting && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}
              Delete claim
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Card className="border-neutral-200 rounded-2xl">
        <CardContent className="p-3 space-y-2">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-neutral-400">Claim</div>
              <div className="text-lg font-extrabold text-neutral-900">{claim.claim_date}</div>
              <div className="text-[11px] text-neutral-500">Market: {claim.market_type_code}</div>
            </div>
            <Badge className={"border text-[10px] " + (STATUS_STYLE[claim.status] || STATUS_STYLE.draft)}>
              {claim.status.replace("_", " ")}
            </Badge>
          </div>
        </CardContent>
      </Card>

      {/* Items */}
      <div className="space-y-2" data-testid="tada-item-list">
        {claim.items.map(it => (
          <Card key={it.id} className="border-neutral-200 rounded-xl"
                data-testid={`tada-item-${it.item_type}-${it.id}`}>
            <CardContent className="p-3 flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                {it.item_type === "da" && (
                  <>
                    <div className="text-[10px] text-[#6D2C91] uppercase tracking-widest flex items-center gap-1">
                      <Wallet className="h-3 w-3" /> DA · auto
                    </div>
                    <div className="text-sm text-neutral-800">{it.market_type_code}</div>
                    <ItemAmount claimed={it.da_amount_inr} item={it} />
                  </>
                )}
                {it.item_type === "travel" && (
                  <>
                    <div className="text-[10px] text-neutral-500 uppercase tracking-widest flex items-center gap-1">
                      <Bike className="h-3 w-3" /> Travel · {it.mode_code}
                    </div>
                    <div className="text-sm text-neutral-800">
                      {it.km_entered} km × ₹ {it.mode_rate_per_km_snapshot}/km
                    </div>
                    {it.remark && <div className="text-[11px] text-neutral-500 mt-0.5 truncate">{it.remark}</div>}
                    <ItemAmount claimed={it.computed_amount_inr} item={it} />
                  </>
                )}
                {it.item_type === "other" && (
                  <>
                    <div className="text-[10px] text-neutral-500 uppercase tracking-widest flex items-center gap-1">
                      <Package className="h-3 w-3" /> {it.claim_type_code}
                    </div>
                    {it.remark && <div className="text-[11px] text-neutral-500 mt-0.5 truncate">{it.remark}</div>}
                    <ItemAmount claimed={it.expense_amount_inr} item={it} />
                  </>
                )}
              </div>
              {isEditable && (
                <div className="flex flex-col items-end gap-1">
                  {it.item_type === "da" && (
                    <Badge className="bg-[#6D2C91]/10 text-[#6D2C91] border-0 text-[9px] uppercase">auto</Badge>
                  )}
                  <div className="flex gap-1">
                    <Button size="icon" variant="ghost" onClick={() => setEditingItem(it)}
                            data-testid={`tada-item-edit-${it.id}`}>
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    {/* DA lines can be reduced but never deleted — the claim
                        always needs its auto-generated DA line. */}
                    {it.item_type !== "da" && (
                      <Button size="icon" variant="ghost" onClick={() => del(it.id)}
                              data-testid={`tada-item-del-${it.id}`}>
                        <Trash2 className="h-3.5 w-3.5 text-red-500" />
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {isEditable && (
        <div className="grid grid-cols-3 gap-2">
          <Button variant="outline" className="h-11 rounded-xl px-2" onClick={() => setAddingType("travel")}
                  data-testid="tada-add-travel">
            <Bike className="h-4 w-4 mr-1" /> Travel
          </Button>
          <Button variant="outline" className="h-11 rounded-xl px-2" onClick={() => setAddingType("night_stay")}
                  data-testid="tada-add-night-stay">
            <Moon className="h-4 w-4 mr-1" /> Night stay
          </Button>
          <Button variant="outline" className="h-11 rounded-xl px-2" onClick={() => setAddingType("other")}
                  data-testid="tada-add-other">
            <Package className="h-4 w-4 mr-1" /> Other
          </Button>
        </div>
      )}

      {/* Sticky footer with total + submit */}
      <div className="fixed bottom-16 left-0 right-0 mx-3 rounded-2xl border border-neutral-200 bg-white shadow-xl p-3 z-30"
           data-testid="tada-footer">
        <div className="flex items-center justify-between mb-2">
          <div className="text-xs text-neutral-500">
            {claim.any_item_decided ? "Claimed" : "Total"}
          </div>
          <div className={"text-2xl font-black " + (claim.any_item_decided ? "text-neutral-400 line-through" : "text-[#E5197F]")}
               data-testid="tada-total">
            ₹ {claim.total_inr}
          </div>
        </div>
        {claim.any_item_decided && (
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs text-emerald-700 font-medium">Approved</div>
            <div className="text-2xl font-black text-emerald-700" data-testid="tada-total-approved">
              ₹ {claim.total_approved_inr}
            </div>
          </div>
        )}
        {isEditable && (
          <Button className="w-full h-11 tulsi-gradient text-white rounded-xl font-bold"
                  onClick={submit} disabled={submitting || claim.items.length === 0}
                  data-testid="tada-submit">
            {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            <ClipboardList className="h-4 w-4 mr-1.5" /> Submit for approval
          </Button>
        )}
      </div>

      {(addingType || editingItem) && (
        <ItemDrawer claimId={claimId}
                     type={editingItem?.item_type || addingType}
                     existing={editingItem}
                     onClose={() => { setAddingType(null); setEditingItem(null); }}
                     onSaved={() => { setAddingType(null); setEditingItem(null); load(); }} />
      )}
    </div>
  );
}


// ---------------------------------------------------------------------------
// GPS-KM hint button (SE-scoped narrow exception; sees only own trail km)
// ---------------------------------------------------------------------------
function GpsKmButton({ onPick }) {
  const [hint, setHint] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/tada/gps-km-hint").then(r => setHint(r.data))
       .catch(() => setHint(null))
       .finally(() => setLoading(false));
  }, []);
  const disabled = loading || !hint || hint.total_km == null;
  return (
    <Button type="button" variant="outline" size="sm" disabled={disabled}
             onClick={() => onPick(Number(hint.total_km).toFixed(1))}
             className="h-10"
             title={disabled ? "No GPS data for today" : `Use ${hint.total_km} km from your GPS trail`}
             data-testid="tada-use-gps-km">
      <MapPin className="h-3.5 w-3.5 mr-1 text-[#6D2C91]" />
      {loading ? "…" : (hint?.total_km != null ? `${Number(hint.total_km).toFixed(1)} km` : "No GPS")}
    </Button>
  );
}

// ---------------------------------------------------------------------------
// Item drawer (travel | other)
// ---------------------------------------------------------------------------
function ItemDrawer({ claimId, type, existing, onClose, onSaved }) {
  const isTravel = type === "travel";
  const isDa = type === "da";
  // Night Stay is just the "other" claim flow with claim_type_code locked to
  // NIGHT_STAY — same backend item_type="other", no schema change needed.
  const isNightStay = type === "night_stay";
  const [modes, setModes] = useState([]);
  const [claimTypes, setClaimTypes] = useState([]);
  const [travelRates, setTravelRates] = useState([]);
  // Travel fields — From/To are now free-typed text (no city-master picker).
  const [fromPlace, setFromPlace] = useState(existing?.from_place || "");
  const [toPlace, setToPlace] = useState(existing?.to_place || "");
  const [modeCode, setModeCode] = useState(existing?.mode_code || "");
  const [km, setKm] = useState(existing?.km_entered || "");
  // A mode with no configured rate anywhere (policy or legacy) falls back
  // to a manually-typed amount instead of blocking the claim outright.
  const [manualAmount, setManualAmount] = useState(existing?.computed_amount_inr || "");
  // Other fields
  const [claimTypeCode, setClaimTypeCode] = useState(
    existing?.claim_type_code || (isNightStay ? "NIGHT_STAY" : "")
  );
  const [expense, setExpense] = useState(existing?.expense_amount_inr || "");
  const [city, setCity] = useState(existing?.city || "");
  // DA field — the only thing an SE can edit on an auto-generated DA line,
  // and only downward (never above da_amount_inr_max).
  const [daAmount, setDaAmount] = useState(existing?.da_amount_inr ?? "");
  // Common
  const [imageRef, setImageRef] = useState(existing?.image_ref || "");
  const [remark, setRemark] = useState(existing?.remark || "");
  const [saving, setSaving] = useState(false);
  // The SE's own policy group — travel rates here override the legacy
  // travel_rates table (used for both the Travel mode picker's displayed
  // rate and the Night Stay fixed-amount cap), so what's shown always
  // matches what the backend will actually charge.
  const [myPolicy, setMyPolicy] = useState(null);
  const [lodgingCap, setLodgingCap] = useState(0);

  useEffect(() => {
    (async () => {
      const [modeR, otherR, ratesR] = await Promise.all([
        api.get("/lookups?category=travel_modes"),
        api.get("/lookups?category=other_claim_type"),
        api.get("/travel-rates?is_active=true"),
      ]);
      setModes((modeR.data.items || []).filter(x => x.is_active !== false));
      setClaimTypes((otherR.data.items || []).filter(x => x.is_active !== false));
      setTravelRates(ratesR.data.items || []);
    })().catch(e => toast.error(formatApiError(e)));
  }, []);

  useEffect(() => {
    api.get("/travel-policy-groups/mine").then(({ data }) => {
      const p = data?.policy;
      setMyPolicy(p || null);
      if (!isNightStay) return;
      // night_stay.amount_inr (+ enabled) is where this is actually
      // configured; other_allowances.lodging_inr_per_day is a fallback for
      // policies that only set that older field.
      const ns = p?.night_stay;
      const cap = ns?.enabled !== false && Number(ns?.amount_inr || 0) > 0
        ? Number(ns.amount_inr)
        : Number(p?.other_allowances?.lodging_inr_per_day || 0);
      setLodgingCap(cap);
      if (cap > 0 && !existing) setExpense(String(cap));
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNightStay]);

  // Policy-specific travel rates override the legacy travel_rates table —
  // this is also what the backend actually charges (policy_travel_rate()),
  // so the picker must show the same number or it's misleading. A NAMED
  // policy is a self-contained rate book once assigned: a mode it doesn't
  // list must NOT silently borrow a legacy rate the admin never set for
  // this policy tier — that's exactly what made it look like a different
  // policy was being applied. Legacy only applies when there's no policy
  // assigned at all.
  const effectiveRateFor = (mode) => {
    const polRow = (myPolicy?.travel_rates || []).find(r => r.mode_code === mode);
    if (polRow) return { rate_per_km_inr: polRow.rate_per_km_inr, allowed: polRow.allowed !== false, source: "policy" };
    if (myPolicy) return null;
    const legacy = travelRates.find(r => r.mode_code === mode);
    return legacy ? { rate_per_km_inr: legacy.rate_per_km_inr, allowed: true, source: "legacy" } : null;
  };
  const activeRate = effectiveRateFor(modeCode);
  const computed = isTravel && km && activeRate
    ? (parseFloat(km) * parseFloat(activeRate.rate_per_km_inr)).toFixed(2)
    : null;
  // Existing items never let the mode change (Select is disabled below), so
  // whether it's rate-based or manual is fixed by what was snapshotted at
  // creation — not recomputed against today's policy/rates.
  const isManualMode = isTravel && modeCode && (
    existing ? existing.mode_rate_per_km_snapshot == null : !activeRate
  );

  const uploadImage = async (file) => {
    const form = new FormData();
    form.append("file", file);
    form.append("context", "tada");
    try {
      const { data } = await api.post("/photos/upload", form,
        { headers: { "Content-Type": "multipart/form-data" } });
      setImageRef(data.ref);
      toast.success("Image uploaded");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const daMax = existing?.da_amount_inr_max ?? existing?.da_amount_inr ?? 0;

  const save = async () => {
    setSaving(true);
    try {
      if (isDa) {
        const amt = parseFloat(daAmount);
        if (isNaN(amt) || amt < 0) { toast.error("Enter a valid amount"); setSaving(false); return; }
        if (amt > daMax + 0.01) {
          toast.error(`DA can only be reduced — max ₹${daMax}`); setSaving(false); return;
        }
        await api.patch(`/tada/claims/${claimId}/items/${existing.id}`, { da_amount_inr: amt });
        toast.success("Updated");
        onSaved();
        return;
      }
      if (!isTravel && isNightStay && lodgingCap > 0 && parseFloat(expense) > lodgingCap + 0.01) {
        toast.error(`Night stay can only be reduced — max ₹${lodgingCap}/day`); setSaving(false); return;
      }
      if (isNightStay && !city.trim()) {
        toast.error("City is required"); setSaving(false); return;
      }
      if (existing) {
        if (!isTravel && !isNightStay && !remark.trim()) {
          toast.error("Remark is required"); setSaving(false); return;
        }
        if (isTravel && isManualMode && !manualAmount) {
          toast.error("Amount is required"); setSaving(false); return;
        }
        if (isTravel && !km) {
          toast.error("KM is required"); setSaving(false); return;
        }
        const body = isTravel
          ? {
              from_place: fromPlace.trim(), to_place: toPlace.trim(),
              km_entered: parseFloat(km),
              ...(isManualMode ? { manual_amount_inr: parseFloat(manualAmount) } : {}),
              remark, image_ref: imageRef || null,
            }
          : isNightStay
          ? { expense_amount_inr: parseFloat(expense), remark, image_ref: imageRef, city: city.trim() }
          : { expense_amount_inr: parseFloat(expense), remark, image_ref: imageRef };
        await api.patch(`/tada/claims/${claimId}/items/${existing.id}`, body);
      } else if (isTravel) {
        if (!fromPlace.trim() || !toPlace.trim() || !modeCode) {
          toast.error("From, To and mode required"); setSaving(false); return;
        }
        if (isManualMode && !manualAmount) {
          toast.error("Enter the amount"); setSaving(false); return;
        }
        if (!km) {
          toast.error("KM is required"); setSaving(false); return;
        }
        await api.post(`/tada/claims/${claimId}/items`, {
          item_type: "travel", from_place: fromPlace.trim(), to_place: toPlace.trim(),
          mode_code: modeCode, km_entered: parseFloat(km),
          ...(isManualMode ? { manual_amount_inr: parseFloat(manualAmount) } : {}),
          image_ref: imageRef || null, remark,
        });
      } else {
        if (!claimTypeCode || !expense) {
          toast.error("Type and amount required"); setSaving(false); return;
        }
        if (!isNightStay && (!imageRef || !remark.trim())) {
          toast.error("Image and remark required"); setSaving(false); return;
        }
        await api.post(`/tada/claims/${claimId}/items`, {
          item_type: "other", claim_type_code: claimTypeCode,
          expense_amount_inr: parseFloat(expense), image_ref: imageRef || null, remark,
          city: isNightStay ? city.trim() : null,
        });
      }
      toast.success(existing ? "Updated" : "Added");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={true} onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid={`tada-drawer-${type}`}>
        <DialogHeader>
          <DialogTitle>
            {isDa ? "Edit DA" : `${existing ? "Edit" : "Add"} ${isTravel ? "travel" : isNightStay ? "night stay" : "other"} item`}
          </DialogTitle>
          <DialogDescription>
            {isDa
              ? `You can only reduce DA below the system-computed rate (₹${daMax}), not raise it.`
              : isTravel
              ? "Amount = KM × mode rate. Rate snapshot is locked at add time."
              : "Expense from receipt. Image mandatory."}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {isDa ? (
            <div>
              <Label>Amount (INR)</Label>
              <Input type="number" min="0" max={daMax} step="0.01" value={daAmount}
                     onChange={e => setDaAmount(e.target.value)}
                     data-testid="tada-drawer-da-amount" />
              <div className="mt-1 text-xs text-neutral-500">Max ₹{daMax} (system-computed rate)</div>
            </div>
          ) : isTravel ? (
            <>
              <div>
                <Label>Mode</Label>
                <Select value={modeCode} onValueChange={setModeCode} disabled={!!existing}>
                  <SelectTrigger data-testid="tada-drawer-mode"><SelectValue placeholder="Select…" /></SelectTrigger>
                  <SelectContent>
                    {modes.map(m => {
                      const r = effectiveRateFor(m.code);
                      return (
                        <SelectItem key={m.code} value={m.code} disabled={r && !r.allowed}>
                          {m.label} {r && r.allowed ? `· ₹${r.rate_per_km_inr}/km` : r && !r.allowed ? "(not allowed)" : "(enter amount manually)"}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label>From</Label>
                  <Input value={fromPlace} onChange={e => setFromPlace(e.target.value)}
                         placeholder="e.g. Alang"
                         data-testid="tada-drawer-from" />
                </div>
                <div>
                  <Label>To</Label>
                  <Input value={toPlace} onChange={e => setToPlace(e.target.value)}
                         placeholder="e.g. Bhavnagar"
                         data-testid="tada-drawer-to" />
                </div>
              </div>
              {isManualMode ? (
                <>
                  <div>
                    <Label>Amount (₹)</Label>
                    <Input type="number" min="0" step="1" value={manualAmount}
                           onChange={e => setManualAmount(e.target.value)}
                           data-testid="tada-drawer-manual-amount" />
                    <div className="mt-1 text-xs text-neutral-500">
                      No rate is configured for this mode — enter the amount directly.
                    </div>
                  </div>
                  <div>
                    <Label>Kilometre</Label>
                    <div className="flex gap-2 items-center">
                      <Input type="number" min="0" step="0.1" value={km}
                             onChange={e => setKm(e.target.value)}
                             className="flex-1"
                             data-testid="tada-drawer-km-manual" />
                      <GpsKmButton onPick={(v) => setKm(String(v))} />
                    </div>
                    <div className="mt-1 text-xs text-neutral-500">
                      Not used to compute the amount — just kept on record for this trip.
                    </div>
                  </div>
                </>
              ) : (
                <div>
                  <Label>KM</Label>
                  <div className="flex gap-2 items-center">
                    <Input type="number" min="0" step="0.1" value={km}
                           onChange={e => setKm(e.target.value)}
                           className="flex-1"
                           data-testid="tada-drawer-km" />
                    <GpsKmButton onPick={(v) => setKm(String(v))} />
                  </div>
                  {computed && (
                    <div className="mt-1 text-xs text-[#6D2C91] font-semibold" data-testid="tada-drawer-computed">
                      Computed: ₹ {computed}
                    </div>
                  )}
                </div>
              )}
            </>
          ) : (
            <>
              {!isNightStay && (
              <div>
                <Label>Type</Label>
                <Select value={claimTypeCode} onValueChange={setClaimTypeCode} disabled={!!existing}>
                  <SelectTrigger data-testid="tada-drawer-type"><SelectValue placeholder="Select…" /></SelectTrigger>
                  <SelectContent>
                    {claimTypes.map(t => <SelectItem key={t.code} value={t.code}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              )}
              {isNightStay && (
                <>
                  <div className="text-sm px-3 py-2 rounded-lg bg-[#6D2C91]/5 text-[#6D2C91] font-medium flex items-center gap-2">
                    <Moon className="h-3.5 w-3.5" /> Night stay claim
                  </div>
                  <div>
                    <Label>City</Label>
                    <Input value={city} onChange={e => setCity(e.target.value)}
                           placeholder="City you stayed in"
                           data-testid="tada-drawer-city" />
                  </div>
                </>
              )}
              <div>
                <Label>Amount (INR)</Label>
                <Input type="number" min="0" step="0.01" value={expense}
                       max={isNightStay && lodgingCap > 0 ? lodgingCap : undefined}
                       onChange={e => setExpense(e.target.value)}
                       data-testid="tada-drawer-amount" />
                {isNightStay && lodgingCap > 0 && (
                  <div className="mt-1 text-xs text-neutral-500">
                    Max ₹{lodgingCap}/day (policy rate) — you can claim less, not more.
                  </div>
                )}
              </div>
            </>
          )}
          {!isDa && (
            <>
              <div>
                <Label>{isTravel || isNightStay ? "Image (optional)" : "Image (required)"}</Label>
                <label className="flex items-center gap-2 h-10 px-3 border border-dashed border-neutral-300 rounded-md cursor-pointer text-sm text-neutral-600">
                  <Camera className="h-4 w-4" />
                  {imageRef ? "Image attached ✓" : "Choose image"}
                  <input type="file" accept="image/*" className="hidden"
                         onChange={e => e.target.files?.[0] && uploadImage(e.target.files[0])}
                         data-testid="tada-drawer-image" />
                </label>
              </div>
              <div>
                <Label>{isTravel || isNightStay ? "Remark (optional)" : "Remark (required)"}</Label>
                <Textarea rows={2} value={remark} onChange={e => setRemark(e.target.value)}
                          data-testid="tada-drawer-remark"
                          placeholder={isTravel || isNightStay ? "Optional note" : "Required note"} />
              </div>
            </>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button className="tulsi-gradient text-white" onClick={save} disabled={saving}
                  data-testid="tada-drawer-save">
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            {existing ? "Update" : "Add"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
